import {
  faCheck,
  faCircleNotch,
  faClock,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";

import type { TestRunVersionProgress } from "src/api/endpoints";
import {
  PLACEHOLDER_PROGRESS,
  testRunBreakdownBars,
} from "src/api/testRuns/progress";
import { Breakdown } from "src/base-components/Breakdown";
import { Pill, type PillVariants } from "src/design-system/Pill";
import type { TestRunProgressStatus } from "src/unifiedCopilot/runs/useTestRunProgress";

const STATUS_PILL: Record<
  TestRunProgressStatus,
  { label: string; variant: PillVariants; spin: boolean }
> = {
  running: { label: "Running", variant: "gray", spin: true },
  finished: { label: "Finished", variant: "green", spin: false },
  failed: { label: "Failed", variant: "red", spin: false },
  expired: { label: "Expired", variant: "orange", spin: false },
  unavailable: { label: "Unavailable", variant: "gray", spin: false },
};

const STATUS_ICON = {
  running: faCircleNotch,
  finished: faCheck,
  failed: faWarning,
  expired: faClock,
  unavailable: faWarning,
} as const;

/** Statuses that describe the read rather than the run, so carry no counts. */
const STATUS_NOTE: Partial<Record<TestRunProgressStatus, string>> = {
  expired: "The run outlived the 10 minute limit, so its results are gone.",
  unavailable: "Could not read this run's progress. Still trying.",
};

/**
 * A settled run with no counts has no total for a bar to fill against, so it
 * says where it ended up rather than rendering an empty bar. Only `running`
 * gets the placeholder bar, where an empty one reads as "nothing done yet".
 */
const MISSING_COUNTS_NOTE: Partial<Record<TestRunProgressStatus, string>> = {
  finished: "The run finished without reporting row counts.",
  failed: "The run failed without reporting row counts.",
};

/**
 * Statuses whose results are worth opening. A run still going has nothing to
 * show yet, and an expired or unreadable one has nothing left to show.
 */
const LINKABLE_STATUSES: ReadonlySet<TestRunProgressStatus> = new Set([
  "finished",
  "failed",
]);

const summarize = (progress: TestRunVersionProgress): string => {
  const processed =
    progress.successful +
    progress.failed +
    progress.ignored +
    progress.output_mismatch;
  const breakdown = [
    { label: "successful", value: progress.successful },
    { label: "failed", value: progress.failed },
    { label: "output mismatch", value: progress.output_mismatch },
    { label: "ignored", value: progress.ignored },
  ]
    .filter(({ value }) => value > 0)
    .map(({ label, value }) => `${value} ${label}`);

  return [`${processed} of ${progress.total} rows`, ...breakdown].join(" · ");
};

/**
 * Presentational card for one dataset test run. Takes whatever counts the
 * caller has — live from the run API, or final from the tool artifact.
 */
export const TestRunCard: React.FC<{
  status: TestRunProgressStatus;
  progress: TestRunVersionProgress | null;
  dataLoc?: string;
  /** Shown once the run has results to open, e.g. a link to them. */
  action?: React.ReactNode;
}> = ({ status, progress, dataLoc, action }) => {
  const pill = STATUS_PILL[status];
  const note =
    STATUS_NOTE[status] ??
    (progress === null ? MISSING_COUNTS_NOTE[status] : undefined);

  return (
    <div
      className="flex flex-col gap-y-1.5 rounded-lg border border-gray-200 px-2 py-1.5"
      data-loc={dataLoc}
    >
      <div className="flex items-center justify-between gap-x-1">
        <span className="min-w-0 truncate font-inter-medium-12px text-gray-800">
          Dataset test run
        </span>
        <Pill size="sm" variant={pill.variant}>
          <Pill.Icon icon={STATUS_ICON[status]} spin={pill.spin} />
          <Pill.Text>{pill.label}</Pill.Text>
        </Pill>
      </div>
      {note !== undefined ? (
        <span className="font-inter-normal-12px text-gray-500">{note}</span>
      ) : (
        <>
          <Breakdown
            bars={testRunBreakdownBars(progress ?? PLACEHOLDER_PROGRESS)}
          />
          <span className="font-inter-normal-12px text-gray-500 tabular-nums">
            {progress === null ? "Starting run…" : summarize(progress)}
          </span>
        </>
      )}
      {action && LINKABLE_STATUSES.has(status) && (
        <div className="flex">{action}</div>
      )}
    </div>
  );
};
